import { useEffect, useRef, useState } from "preact/hooks";
import type { Action } from "@cos/shared";
import type { WidgetMessage } from "../state.js";
import { renderBlock } from "../render/blocks.js";
import { renderMarkdown } from "../render/markdown.js";
import { DomMount } from "./DomMount.js";

export interface MessageListProps {
  messages: WidgetMessage[];
  botTyping: boolean;
  /** The bot's configured greeting, shown as a leading bubble ahead of `messages` — never persisted, never counted toward `interactive`/feedback. */
  greeting?: string;
  /** Opens the full-size viewer — hoisted to the panel so the overlay isn't clipped by the scrolling transcript. */
  onOpenImage: (url: string) => void;
  onQuickReply: (value: string) => void;
  onAction: (action: Action) => void;
  onOrderConfirm: (orderId: string) => void;
  onFormSubmit: (fields: Record<string, string>) => void;
  /**
   * -1 or 1, per assistant message. Thumbs are offered on any assistant
   * reply, independently of `collect_feedback`. `comment` arrives on a second
   * call, after the shopper has written one — the rating is never held
   * hostage to the comment, and the server upserts the two onto one row.
   */
  onFeedback: (messageId: string, rating: number, comment?: string) => void;
}

/**
 * What the shopper has said about a reply, this session.
 *
 * Local rather than in `WidgetState`: nothing else reads it, it is honestly
 * per-session (a reload starts a new conversation with the widget anyway),
 * and the store is the more expensive place to put it under the bundle
 * budget. `App` never unmounts this component, so it survives re-render.
 */
interface RatingState {
  rating: number;
  stage: "prompting" | "done";
}

export function MessageList(props: MessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [ratings, setRatings] = useState<Record<string, RatingState>>({});
  const [draft, setDraft] = useState("");

  function rate(messageId: string, rating: number) {
    // Sent immediately. A shopper who taps a thumb and wanders off has still
    // told us something, and waiting for a comment would lose it.
    props.onFeedback(messageId, rating);
    setDraft("");
    setRatings((current) => ({ ...current, [messageId]: { rating, stage: "prompting" } }));
  }

  function sendComment(messageId: string, rating: number) {
    const comment = draft.trim();
    if (comment.length > 0) props.onFeedback(messageId, rating, comment);
    setDraft("");
    setRatings((current) => ({ ...current, [messageId]: { rating, stage: "done" } }));
  }

  useEffect(() => {
    const el = containerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [props.messages, props.botTyping]);

  /**
   * The transcript is pinned to the newest message on every new message, but
   * not on every new *height* — and a keyboard opening shortens the panel by
   * a third without saying anything. Re-pinning on a shrink is what keeps the
   * reply the shopper is halfway through reading on screen instead of above
   * it. Growth is left alone: the panel getting taller reveals more of the
   * same view, and scrolling then would yank the transcript for no reason.
   */
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let previous = el.clientHeight;
    const observer = new ResizeObserver(() => {
      const height = el.clientHeight;
      const shrank = height < previous;
      previous = height;
      if (shrank) el.scrollTop = el.scrollHeight;
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div class="cos-messages" ref={containerRef}>
      {props.greeting && props.greeting.length > 0 && (
        <div class="cos-message" data-role="assistant">
          <div class="cos-bubble">
            <DomMount node={renderMarkdown(props.greeting)} />
          </div>
        </div>
      )}
      {props.messages.map((message, index) => (
        <div class="cos-message" data-role={message.role} key={message.id}>
          <div class="cos-bubble">
            {message.attachments && message.attachments.length > 0 && (
              <div class="cos-message-images">
                {message.attachments.map((attachment) => (
                  // A thumbnail in a 320px-wide panel is unreadable for
                  // anything with text on it, which is most of what people
                  // send — so every one of them opens.
                  <button type="button" key={attachment.id} class="cos-image-button" onClick={() => props.onOpenImage(attachment.url)}>
                    <img src={attachment.url} alt="Attached image" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
            {message.text.length > 0 && <DomMount key="text" node={renderMarkdown(message.text)} />}
            {message.blocks?.map((block, blockIndex) => (
              <DomMount
                key={blockIndex}
                node={renderBlock(
                  block,
                  {
                    onQuickReply: props.onQuickReply,
                    onAction: props.onAction,
                    onOrderConfirm: props.onOrderConfirm,
                    onFormSubmit: props.onFormSubmit,
                  },
                  // A follow-up on any message but the last is history, not
                  // a live prompt — tapping it would answer a question the
                  // conversation has already moved past.
                  { interactive: index === props.messages.length - 1 },
                )}
              />
            ))}
          </div>
          {/* Thumbs on any assistant message, whether or not the bot asked
              — the cheapest signal there is, and the one clients read. */}
          {message.role === "assistant" && !message.pending && message.seq !== undefined && (
            <div class="cos-feedback">
              <div class="cos-feedback-thumbs">
                <button
                  type="button"
                  aria-label="Helpful"
                  aria-pressed={ratings[message.id]?.rating === 1}
                  data-active={ratings[message.id]?.rating === 1 ? "true" : undefined}
                  onClick={() => rate(message.id, 1)}
                >
                  &#128077;
                </button>
                <button
                  type="button"
                  aria-label="Not helpful"
                  aria-pressed={ratings[message.id]?.rating === -1}
                  data-active={ratings[message.id]?.rating === -1 ? "true" : undefined}
                  onClick={() => rate(message.id, -1)}
                >
                  &#128078;
                </button>
              </div>

              {/* The thumb is the signal; the comment is what makes it
                  actionable. Asked for after either rating — "what made this
                  good" is as useful to a prompt as "what was missing" — and
                  always dismissible, because a shopper mid-purchase owes us
                  nothing. */}
              {ratings[message.id]?.stage === "prompting" && (
                <form
                  class="cos-feedback-comment"
                  onSubmit={(event) => {
                    event.preventDefault();
                    sendComment(message.id, ratings[message.id]!.rating);
                  }}
                >
                  <input
                    type="text"
                    value={draft}
                    maxLength={2000}
                    autoComplete="off"
                    aria-label={ratings[message.id]!.rating > 0 ? "What helped?" : "What was missing?"}
                    placeholder={ratings[message.id]!.rating > 0 ? "What helped? (optional)" : "What was missing? (optional)"}
                    onInput={(event) => setDraft((event.target as HTMLInputElement).value)}
                  />
                  <button type="submit" disabled={draft.trim().length === 0}>
                    Send
                  </button>
                  <button
                    type="button"
                    class="cos-feedback-dismiss"
                    aria-label="No thanks"
                    onClick={() => setRatings((current) => ({ ...current, [message.id]: { rating: current[message.id]!.rating, stage: "done" } }))}
                  >
                    &times;
                  </button>
                </form>
              )}

              {ratings[message.id]?.stage === "done" && <span class="cos-feedback-thanks">Thanks.</span>}
            </div>
          )}
        </div>
      ))}
      {props.botTyping && (
        <div class="cos-message" data-role="assistant">
          <div class="cos-bubble cos-typing">
            <span />
            <span />
            <span />
          </div>
        </div>
      )}
    </div>
  );
}
